import React, { useContext, useEffect, useRef, useState } from 'react';
import { FaStar, FaUserCircle, FaVideo } from 'react-icons/fa';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { RiVerifiedBadgeFill } from 'react-icons/ri';
import { LuAward, LuBuilding2, LuVideo } from 'react-icons/lu';
import { Link, useNavigate } from 'react-router-dom';
import { MyContext } from '../../Context/MyProvider';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Loading from '../Loading';
import secureLocalStorage from "react-secure-storage";
import ImageLoad from '../ImageLoad';

/* ─── Reusable Doctor Card ─────────────────────────────────────────────── */
const DoctorCard = ({ doc, currency, isVideo, onNavigate, t }) => {
    const price = isVideo
        ? `${currency}${doc.min_vid_price} – ${currency}${doc.max_vid_price}`
        : `${currency}${doc.min_inp_price} – ${currency}${doc.max_inp_price}`;

    return (
        <div
            onClick={onNavigate}
            className="group bg-white rounded-2xl border border-slate-100 hover:border-indigo-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-pointer"
        >
            <div className="p-4 sm:p-5">
                {/* Top row: avatar + info */}
                <div className="flex items-start gap-3 mb-4">
                    {/* Avatar */}
                    <div className="relative shrink-0">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-gradient-to-br from-indigo-50 to-slate-100 ring-2 ring-white shadow-sm">
                            <ImageLoad src={doc.logo} CustomeStyle="object-cover w-full h-full" alt="Doctor" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white" title="Online" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                        {/* Specialty badge */}
                        <span className="inline-block text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md mb-1">
                            {doc.title}
                        </span>
                        <h3 className="text-sm sm:text-base font-bold text-slate-800 line-clamp-1 group-hover:text-indigo-700 transition-colors">
                            {doc.name || doc.title}
                        </h3>
                        <p className="text-[11px] text-slate-400 line-clamp-2 mt-0.5 leading-relaxed">
                            {doc.subtitle}
                        </p>
                    </div>
                </div>

                {/* Stats row */}
                <div className="flex items-center gap-3 text-xs mb-4">
                    {/* Rating */}
                    {doc.avg_star !== 0 ? (
                        <div className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded-lg font-semibold">
                            <FaStar className="text-green-500 text-[10px]" />
                            <span>{doc.avg_star}</span>
                            <span className="text-green-400 font-normal hidden sm:inline">({doc.tot_review})</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-1 text-slate-400">
                            <FaStar className="text-[10px]" />
                            <span>{t("No review")}</span>
                        </div>
                    )}

                    {/* Experience */}
                    <div className="flex items-center gap-1 text-slate-500">
                        <LuAward className="text-indigo-400 text-sm shrink-0" />
                        <span>{doc.year_of_experience} {t("yrs")}</span>
                    </div>
                </div>

                {/* Fee row */}
                <div className="flex items-center gap-2.5 bg-slate-50 rounded-xl px-3 py-2.5 mb-4">
                    {isVideo
                        ? <LuVideo className="text-indigo-500 text-base shrink-0" />
                        : <LuBuilding2 className="text-indigo-500 text-base shrink-0" />
                    }
                    <div>
                        <p className="text-[10px] text-slate-400 font-medium leading-none mb-0.5">
                            {isVideo ? t("Video Visit") : t("Consultation Fee")}
                        </p>
                        <p className="text-sm font-bold text-slate-800">{price}</p>
                    </div>
                </div>

                {/* Book CTA */}
                <button className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white text-xs font-extrabold tracking-wider rounded-xl transition-all duration-200 uppercase shadow-sm hover:shadow-md">
                    {t("Book Appointment")}
                </button>
            </div>
        </div>
    );
};

/* ─── Main Component ───────────────────────────────────────────────────── */
const DocDetail = () => {

    const { t } = useTranslation();
    const { ApiBaseUrl, ImgBaseUrl } = useContext(MyContext);
    const site_currency = secureLocalStorage.getItem("currency");
    const currency = site_currency ? site_currency : "$";

    const [personeData, setPersoneData] = useState([]);
    const [videoData, setVideoData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [apiloading, setApiLoading] = useState(false);
    const [page, setPage] = useState(1);

    const navigate = useNavigate();
    const hasReachedBottom = useRef(false);
    const NoMoreDataRef = useRef(true);
    const selectBtnRef = useRef(0);

    const Data = sessionStorage.getItem("Docdetails");
    if (Data) {
        var { title, path, pagename, id } = JSON.parse(Data);
    }

    const NavigateHandle = (title, Id) => {
        navigate(`/doctor-profile/${id}/${Id}`);
        sessionStorage.setItem("Docprofile", JSON.stringify({ id: Id, department_id: id, title: title, path: "/doctor-details", pagename: "Doctor Details" }));
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        HandlefetchInPersonData(1);
    }, []);

    const HandlefetchInPersonData = (page) => {
        setApiLoading(true);
        navigator.geolocation.getCurrentPosition(position => {
            const lat = String(position.coords.latitude);
            const long = String(position.coords.longitude);
            axios.post(`${ApiBaseUrl}/customer/dep_type_client_doctor`, { department_id: id, lat: lat, lon: long, page })
                .then((res) => {
                    const { clinic_visit, ResponseCode } = res.data;
                    console.log("dep_type_client_doctor", res);
                    if (ResponseCode === 200) {
                        if (clinic_visit.length === 0) NoMoreDataRef.current = false;
                        if (page === 1) {
                            setPersoneData(clinic_visit);
                        } else {
                            setPersoneData(prev => [...prev, ...clinic_visit]);
                        }
                    }
                    setLoading(false);
                    setApiLoading(false);
                })
                .catch((error) => { console.error(error); });
        });
    };

    const HandlefetchVideoData = (page) => {
        navigator.geolocation.getCurrentPosition(position => {
            const lat = String(position.coords.latitude);
            const long = String(position.coords.longitude);
            axios.post(`${ApiBaseUrl}/customer/dep_type_video_doctor`, { department_id: id, lat: lat, lon: long, page })
                .then((res) => {
                    const { video_consult, ResponseCode } = res.data;
                    if (ResponseCode === 200) {
                        if (video_consult.length === 0) NoMoreDataRef.current = false;
                        if (page === 1) {
                            setVideoData(video_consult);
                        } else {
                            setVideoData(prev => [...prev, ...video_consult]);
                        }
                    }
                    setApiLoading(false);
                })
                .catch((error) => { console.error(error); });
        });
    };

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
            const clientHeight = window.innerHeight;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
            if (isAtBottom && !hasReachedBottom.current && NoMoreDataRef.current) {
                hasReachedBottom.current = true;
                setPage(prev => prev + 1);
            } else if (!isAtBottom && hasReachedBottom.current) {
                hasReachedBottom.current = false;
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => { window.removeEventListener('scroll', handleScroll); };
    }, []);

    useEffect(() => {
        if (page === 1 || !NoMoreDataRef.current) return;
        if (selectBtnRef.current === 0) {
            HandlefetchInPersonData(page);
        } else {
            HandlefetchVideoData(page);
        }
    }, [page]);

    const HandleSelectButton = (index) => {
        selectBtnRef.current = index;
        NoMoreDataRef.current = true;
        setPage(1);
        setApiLoading(true);
        if (index === 0) {
            HandlefetchInPersonData(1);
        } else {
            HandlefetchVideoData(1);
        }
    };

    const isVideo = selectBtnRef.current === 1;
    const activeList = isVideo ? videoData : personeData;

    return (
        <div>
            {loading
                ? <Loading CustomeStyle="fixed left-0 top-0 h-[100vh] z-[999] w-full flex items-center justify-center bg-white" />
                : (
                    <div className="width-container details max-_430_:pb-0 pb-10">

                        {/* ── Header bar ── */}
                        <div className="flex items-center justify-between top-section">
                            <Link
                                to="/"
                                className="flex items-center gap-[5px] border-[2px] border-gray-200 rounded-[50px] px-[10px] group py-[4px] hover:bg-black"
                            >
                                <IoIosArrowBack className="group-hover:text-white" />
                                <h1 className="font-[500] group-hover:text-white">{t("Go Home")}</h1>
                            </Link>
                            <div className="flex items-center gap-[10px] max-_430_:hidden">
                                <Link to={path} className="font-[500] text-gray-700">{t(pagename)}</Link>
                                <IoIosArrowForward />
                                <h1 className="font-[500] text-gray-400 pagename">{title}</h1>
                            </div>
                            <div className="border-[2px] border-gray-200 rounded-[50px] px-[20px] py-[2px] hidden max-_430_:block">
                                <Link to={path} className="font-[500] text-gray-600">{t("Back")}</Link>
                            </div>
                        </div>

                        {/* ── Section title ── */}
                        <div className="mt-6 mb-2">
                            <h2 className="text-2xl font-bold text-slate-800 tracking-tight">{title}</h2>
                            <p className="text-sm text-slate-400 mt-1">{t("Choose a consultation type to browse available doctors.")}</p>
                        </div>

                        {/* ── Tab switcher ── */}
                        <div className="flex justify-center mt-5">
                            <div className="inline-flex bg-slate-100 rounded-2xl p-1 gap-1 shadow-inner">
                                <button
                                    onClick={() => HandleSelectButton(0)}
                                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${selectBtnRef.current === 0
                                        ? "bg-white text-indigo-700 shadow-sm"
                                        : "text-slate-500 hover:text-slate-700"
                                        }`}
                                >
                                    <FaUserCircle className="text-base" />
                                    {t("In Person")}
                                </button>
                                <button
                                    onClick={() => HandleSelectButton(1)}
                                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${selectBtnRef.current === 1
                                        ? "bg-white text-indigo-700 shadow-sm"
                                        : "text-slate-500 hover:text-slate-700"
                                        }`}
                                >
                                    <FaVideo className="text-base" />
                                    {t("Video Visit")}
                                </button>
                            </div>
                        </div>

                        {/* ── Doctor cards ── */}
                        {apiloading
                            ? <Loading CustomeStyle="h-[400px] w-full flex items-center justify-center bg-white" />
                            : (
                                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {activeList.length >= 1
                                        ? activeList.map((doc, index) => (
                                            <DoctorCard
                                                key={index}
                                                doc={{ ...doc, logo: ImgBaseUrl + doc.logo }}
                                                currency={currency}
                                                isVideo={isVideo}
                                                onNavigate={() => NavigateHandle(doc.title, doc.id)}
                                                t={t}
                                            />
                                        ))
                                        : (
                                            <div className="col-span-full flex flex-col items-center justify-center h-[300px] text-center">
                                                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                                                    <FaUserCircle className="text-3xl text-slate-300" />
                                                </div>
                                                <h3 className="font-bold text-slate-600 text-lg">{t("No Doctor available in your area.")}</h3>
                                                <p className="text-slate-400 text-sm mt-1">{t("Try switching to another consultation type.")}</p>
                                            </div>
                                        )
                                    }
                                </div>
                            )
                        }
                    </div>
                )
            }
        </div>
    );
};

export default DocDetail;